import React, { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "../utils/supabase";
import { fetchWithAuthRetry } from "../utils/withTimeout";
import { isBadUsername } from '../utils/blockedWords';

const TEE_SIZES = ['S', 'M', 'L', 'XL', 'XXL'];

// StreakWards claim form. Mirrors the FlagModal pattern in ResultsScreen.jsx:
// email-first (notify Matt) THEN stamp claimed via the claim_streakward RPC, so
// a failed email never burns the claim (worst case: user re-submits).
const ClaimModal = ({ tier, onClaimed, onClose }) => {
  const [fullName, setFullName] = useState('');
  const [size, setSize] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [favoriteSchool, setFavoriteSchool] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');

  // Hoodie tier retired 2026-09-20 (out of stock): the tee is the only prize.
  const prizeName = 'Heavyweight T-Shirt';
  const canSend = fullName.trim() && size && shippingAddress.trim();

  const handleSend = async () => {
    if (!canSend || sending) return;
    setSending(true);
    setErr('');
    try {
      // The route derives username, email and user id from this token, so the
      // reply-to address on Matt's notification can't be spoofed.
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('no session');
      const resp = await fetch('/api/streakwards-claim', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          tier,
          fullName: fullName.trim(),
          size,
          shippingAddress: shippingAddress.trim(),
          favoriteSchool: favoriteSchool.trim(),
        }),
      });
      if (!resp.ok) throw new Error('email failed');
      // Stamp claimed only after the notify email is confirmed sent.
      const { error } = await supabase.rpc('claim_streakward', { p_tier: tier });
      if (error) throw error;
      setSent(true);
      if (onClaimed) await onClaimed();
    } catch (e) {
      console.error('StreakWards claim error:', e);
      setErr('Something went wrong. Please try again.');
    }
    setSending(false);
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', background: '#111b30',
    border: '1px solid var(--border-dark)', borderRadius: 'var(--radius)',
    color: 'var(--text-white)', fontFamily: 'var(--font-heading)',
    fontSize: '13px', letterSpacing: '1px', marginBottom: '12px',
    boxSizing: 'border-box',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 999, padding: '20px',
    }}>
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border-dark)',
        borderRadius: 'var(--radius)', padding: '24px', width: '100%',
        maxWidth: '360px', maxHeight: '90vh', overflowY: 'auto',
      }}>
        {sent ? (
          <>
            <div style={{ textAlign: 'center', fontFamily: 'var(--font-heading)', fontSize: '20px', color: 'var(--green-correct)', letterSpacing: '2px', marginBottom: '8px' }}>✓ CLAIM SENT</div>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '12px', color: 'var(--text-dim)', textAlign: 'center', marginBottom: '6px' }}>{`Your ${prizeName} is on the way. Matt will be in touch.`}</p>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '10px', lineHeight: 1.45, color: 'var(--text-dim)', opacity: 0.8, textAlign: 'center', marginBottom: '16px' }}>Prizes are while supplies last. Allow a few weeks for delivery.</p>
            <button className="btn btn-secondary" style={{ width: '100%' }} onClick={onClose}>CLOSE</button>
          </>
        ) : (
          <>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: '14px', letterSpacing: '2px', color: 'var(--gold)', marginBottom: '8px' }}>{`CLAIM YOUR ${prizeName.toUpperCase()}`}</div>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '12px', color: 'var(--text-dim)', marginBottom: '4px' }}>Where do we send it?</p>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '10px', lineHeight: 1.45, color: 'var(--text-dim)', opacity: 0.8, marginBottom: '16px' }}>Prizes are while supplies last. Allow a few weeks for delivery.</p>
            <input type="text" placeholder="Full name" value={fullName} onChange={e => setFullName(e.target.value)} style={inputStyle} autoFocus />
            <select value={size} onChange={e => setSize(e.target.value)} style={inputStyle}>
              <option value="">T-shirt size...</option>
              {TEE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <textarea placeholder="Shipping address" rows={3} value={shippingAddress} onChange={e => setShippingAddress(e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} />
            <input type="text" placeholder="Favorite school" value={favoriteSchool} onChange={e => setFavoriteSchool(e.target.value)} style={inputStyle} />
            {err && <p style={{ fontFamily: 'var(--font-body)', fontSize: '11px', color: 'var(--red-incorrect)', marginBottom: '8px' }}>{err}</p>}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>CANCEL</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleSend} disabled={sending || !canSend}>{sending ? 'SENDING...' : 'SEND'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Profile = ({ user, authReady, onUsernameChange }) => {
  const [profile, setProfile] = useState(null);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ok' | 'error'
  // Scouting Report payload from get_my_accuracy(); null = hide the section.
  const [accuracy, setAccuracy] = useState(null);
  const [editingUsername, setEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  // Which prize tier the claim modal is open for ('tee' | null).
  // Captured on open so it stays stable through the sent state.
  const [claimTier, setClaimTier] = useState(null);

  const handleSaveUsername = async () => {
    const trimmed = newUsername.trim();
    if (!trimmed || trimmed.length < 3) { setSaveError('Must be at least 3 characters.'); return; }
    if (trimmed.length > 20) { setSaveError('Max 20 characters.'); return; }
    if (!/^[a-zA-Z0-9_]+$/.test(trimmed)) { setSaveError('Letters, numbers, underscores only.'); return; }
if (isBadUsername(trimmed)) { setSaveError('That username isn\'t allowed. Please choose another.'); return; }
setSaving(true);
    setSaveError('');
    const { error } = await supabase
      .from('profiles')
      .update({ username: trimmed })
      .eq('id', user.id);
    if (error) {
      setSaveError('Username already taken.');
    } else {
      setProfile(prev => ({ ...prev, username: trimmed }));
      setEditingUsername(false);
      if (onUsernameChange) onUsernameChange(trimmed);
    }
    setSaving(false);
  };

  const userId = user?.id;
  const hasLoadedRef = useRef(false);
  const loadProfile = useCallback(async () => {
    if (!userId) return;
    // Keyed on userId (not the whole user object) so the resume cascade's
    // currentUser churn - new objects with the same id - does not re-run this
    // or reset the view. Blank to LOADING only on the genuine first load.
    if (!hasLoadedRef.current) setStatus('loading');
    const PROFILE_COLS = "username, wins, losses, games_played, current_streak, longest_streak, nba_current_streak, nba_longest_streak, mosh_current_streak, mosh_longest_streak, ntg_current_streak, ntg_longest_streak, ntg_strikes, ntg_sr_current_streak, ntg_sr_longest_streak, ntg_sr_strikes, digits_current_streak, digits_longest_streak, digits_strikes, ironman_current_streak, ironman_longest_streak, created_at, tee_banked_at, tee_claimed_at, hoodie_banked_at, hoodie_claimed_at";
    const readProfile = () => fetchWithAuthRetry(() => supabase
      .from("profiles")
      .select(PROFILE_COLS)
      .eq("id", userId)
      .maybeSingle());
    try {
      // maybeSingle, not single: a missing row used to come back as a 406 and
      // strand this page on "COULDN'T LOAD" forever. Now no row is a value we
      // can act on, so we self-heal and retry once.
      let { data, error } = await readProfile();
      if (error) throw error;
      if (!data) {
        // Server-side heal: ensure_profile() copies created_at from auth.users,
        // which the browser cannot see or forge. Sending our own timestamp here
        // would let an old account look brand-new to the attribution gate.
        await supabase.rpc('ensure_profile');
        ({ data, error } = await readProfile());
        if (error) throw error;
      }
      if (!data) throw new Error('profile row unavailable after heal');
      setProfile(data);
      hasLoadedRef.current = true;
      setStatus('ok');
      // Scouting Report: separate, fail-soft fetch. auth.uid() inside the RPC
      // scopes it to this user; a null/error just hides the section.
      try {
        supabase.rpc('get_my_accuracy')
          .then(({ data: acc, error: accErr }) => { if (!accErr && acc) setAccuracy(acc); })
          .catch(() => {});
      } catch {}
    } catch (err) {
      console.error('Profile load error:', err);
      // Surface the error UI only if we have nothing to show; on a failed
      // revalidate keep the existing data instead of blanking.
      if (!hasLoadedRef.current) setStatus('error');
    }
  }, [userId]);

  useEffect(() => { if (authReady) loadProfile(); }, [authReady, loadProfile]);

  if (!profile) return (
    <div className="profile-card">
      <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-dim)', fontFamily: 'var(--font-heading)', fontSize: '12px', letterSpacing: '2px' }}>
        {status === 'error' ? (
          <>
            COULDN'T LOAD.{' '}
            <button onClick={loadProfile} style={{ background: 'none', border: 'none', color: 'var(--gold)', textDecoration: 'underline', cursor: 'pointer', font: 'inherit', letterSpacing: '2px' }}>RETRY</button>
          </>
        ) : 'LOADING...'}
      </div>
    </div>
  );

  const winPct = profile.games_played > 0
    ? Math.round((profile.wins / profile.games_played) * 100)
    : 0;

  const joined = profile.created_at
    ? new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : '—';

  // StreakWards: the tee is the only prize (Matt, 2026-09-20 - hoodies are out
  // of stock and the tier is retired). A bank is NOT permanent any more: the
  // nightly expire_streakwards() sweep nulls tee_banked_at once the window
  // lapses, so an unclaimed prize is re-earnable only on a brand new streak.
  const teeBanked = !!profile.tee_banked_at;
  const teeClaimed = !!profile.tee_claimed_at;

  // 10-day claim window (Matt, 2026-08-24). Must stay in lockstep with
  // claim_streakward() and the streakward-expiry-reminder function; the
  // 2026-08-25 grandfather epoch is gone, since the sweep clears lapsed banks
  // outright rather than leaving them to be filtered here.
  const CLAIM_WINDOW_DAYS = 10;
  const claimDaysLeft = (bankedAt) => {
    if (!bankedAt) return null;
    const expires = new Date(bankedAt).getTime() + CLAIM_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    return Math.ceil((expires - Date.now()) / (24 * 60 * 60 * 1000));
  };
  const teeDaysLeft = teeBanked && !teeClaimed ? claimDaysLeft(profile.tee_banked_at) : null;
  const teeExpired = teeDaysLeft !== null && teeDaysLeft <= 0;
  // One prize per player, ever (Matt, 2026-08-24).
  const allClaimed = teeClaimed;
  const nextClaimable = teeClaimed ? null : (teeBanked && !teeExpired) ? 'tee' : null;

  return (
    <div className="profile-card">
      <div className="profile-header">
        <div className="profile-avatar">
          {profile.username?.[0]?.toUpperCase() || '?'}
        </div>
        <div className="profile-identity">
          {editingUsername ? (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '4px' }}>
    <input
      value={newUsername}
      onChange={e => { setNewUsername(e.target.value); setSaveError(''); }}
      maxLength={20}
      style={{
        fontFamily: 'var(--font-heading)', fontSize: '14px', letterSpacing: '1px',
        background: 'var(--bg-navy)', border: '1px solid var(--gold)',
        color: 'white', padding: '4px 8px', borderRadius: '4px', width: '160px'
      }}
      autoFocus
    />
    {saveError && <span style={{ fontFamily: 'var(--font-body)', fontSize: '11px', color: 'var(--red-incorrect)' }}>{saveError}</span>}
    <div style={{ display: 'flex', gap: '6px' }}>
      <button
        onClick={handleSaveUsername}
        disabled={saving}
        style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', letterSpacing: '1px', background: 'var(--gold)', color: 'black', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer' }}
      >
        {saving ? '...' : 'SAVE'}
      </button>
      <button
        onClick={() => { setEditingUsername(false); setSaveError(''); }}
        style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', letterSpacing: '1px', background: 'none', color: 'var(--text-dim)', border: '1px solid var(--border-dark)', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer' }}
      >
        CANCEL
      </button>
    </div>
  </div>
) : (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <div className="profile-username">{profile.username}</div>
    <button
  onClick={() => { setNewUsername(profile.username); setEditingUsername(true); }}
  style={{ fontFamily: 'var(--font-heading)', fontSize: '8px', letterSpacing: '1px', background: 'var(--gold)', color: 'black', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', display: 'block', marginTop: '6px' }}
>
  EDIT
</button>
  </div>
)}
          <div className="profile-email">{user.email}</div>
          <div className="profile-joined">MEMBER SINCE {joined.toUpperCase()}</div>

          {/* StreakWards claim CTA */}
          <div style={{ marginTop: '8px' }}>
            {allClaimed ? (
              <span style={{ fontFamily: 'var(--font-heading)', fontSize: '10px', letterSpacing: '1px', color: 'var(--green-correct)' }}>✓ PRIZE CLAIMED</span>
            ) : (
              <div style={{ display: 'inline-block' }}>
                <button
                  onClick={() => { if (nextClaimable) setClaimTier(nextClaimable); }}
                  disabled={!nextClaimable}
                  style={{ fontFamily: 'var(--font-heading)', fontSize: '10px', letterSpacing: '1px', background: 'var(--gold)', color: 'black', border: 'none', borderRadius: '4px', padding: '7px 12px', cursor: nextClaimable ? 'pointer' : 'default', opacity: nextClaimable ? 1 : 0.45, display: 'block', width: '100%' }}
                >
                  CLAIM YOUR SWEET PRIZE
                </button>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: '9px', color: 'var(--text-dim)', marginTop: '2px', textAlign: 'center' }}>(Notify Matt)</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* StreakWards prize tiers */}
      <div style={{ padding: '8px 12px', background: 'var(--bg-navy)', borderTop: '1px solid var(--border-dark)' }}>
        <div style={{ fontFamily: 'var(--font-heading)', fontSize: '9px', letterSpacing: '3px', color: 'var(--text-dim)', marginBottom: '2px', textAlign: 'center' }}>STREAKWARDS | CURRENT LEVELS</div>
        {/* Static on purpose: this is a snapshot stamp, so it only changes when
            the levels do. A live date would always read current and say nothing. */}
        <div style={{ fontFamily: 'var(--font-body)', fontSize: '9px', color: 'var(--text-dim)', opacity: 0.7, marginBottom: '6px', textAlign: 'center' }}>as of September 2026</div>
        {[
          { tier: 'tee', label: '50-Piece Streak, or 125-Piece Streak (LIDS): Heavyweight T-Shirt', banked: teeBanked, claimed: teeClaimed, daysLeft: teeDaysLeft, expired: teeExpired },
        ].map(({ tier, label, banked, claimed, daysLeft, expired }) => (
          <div key={tier} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', padding: '3px 0' }}>
            <span style={{ fontFamily: 'var(--font-body)', fontSize: '11px', color: (claimed || (banked && !expired)) ? 'white' : 'var(--text-dim)' }}>{label}</span>
            <span style={{ fontFamily: 'var(--font-heading)', fontSize: '8px', letterSpacing: '1px', whiteSpace: 'nowrap', color: claimed ? 'var(--text-dim)' : expired ? 'var(--text-dim)' : banked ? (daysLeft <= 3 ? 'var(--red)' : 'var(--green-correct)') : 'var(--text-dim)' }}>
              {claimed ? '✓ CLAIMED'
                : expired ? 'EXPIRED'
                : banked ? `EARNED · ${daysLeft} DAY${daysLeft === 1 ? '' : 'S'} TO CLAIM`
                : 'LOCKED'}
            </span>
          </div>
        ))}
        <p style={{ fontFamily: 'var(--font-body)', fontSize: '9px', lineHeight: 1.45, color: 'var(--text-dim)', margin: '8px 0 0 0', textAlign: 'center' }}>
          Current levels. One prize per player, while supplies last... no more hoodies, only a tee can be won. Earn it and you have 10 days to claim it. Miss that and it expires and resets... you'd have to build a brand new streak and earn it all over again. We'll post here if levels change.
        </p>
      </div>

      {/* Iron Man streak — headline metric (leads the profile) */}
<div style={{ padding: '8px 12px', background: 'var(--bg-navy)', borderTop: '1px solid var(--border-dark)' }}>
  <div style={{ fontFamily: 'var(--font-heading)', fontSize: '9px', letterSpacing: '3px', color: 'var(--text-dim)', marginBottom: '6px', textAlign: 'center' }}>🧢 LIDS | LOCKED IN DAILY STREAK</div>
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
    <div className="profile-stat" style={{ padding: '6px 2px' }}>
      <span className="profile-stat-value" style={{ fontSize: '20px', color: 'white' }}>{profile.ironman_current_streak || '—'}</span>
      <span className="profile-stat-label" style={{ fontSize: '8px' }}>NOW</span>
    </div>
    <div className="profile-stat" style={{ padding: '6px 2px' }}>
      <span className="profile-stat-value" style={{ fontSize: '20px', color: 'white' }}>{profile.ironman_longest_streak || 0}</span>
      <span className="profile-stat-label" style={{ fontSize: '8px' }}>BEST</span>
    </div>
  </div>
</div>

{/* Daily Streaks section — 2x2 grid */}
<div style={{ padding: '8px 12px 4px', background: 'var(--bg-navy)', borderTop: '1px solid var(--border-dark)' }}>
  <div style={{ fontFamily: 'var(--font-heading)', fontSize: '9px', letterSpacing: '3px', color: 'var(--text-dim)', marginBottom: '6px', textAlign: 'center' }}>DAILY STREAKS</div>

  {/* Leaderboard order (Matt, 2026-08-24): NFL/NBA/Mosh on top, then the
      trivia games (Sr., Jr., Digits) at the same compact 3-up size. */}
  {(() => {
    const cell = ({ emoji, label, now, best, strikes }) => (
      <div key={label} style={{ background: 'var(--bg-navy)', border: '1px solid var(--border-dark)', borderRadius: '4px', padding: '6px 4px' }}>
        <div style={{ textAlign: 'center', fontFamily: 'var(--font-heading)', fontSize: '9px', color: 'var(--text-dim)', letterSpacing: '1px', marginBottom: '4px' }}>
          {emoji} {label === 'NTG SR.' || label === 'NTG JR.'
            ? <>NTG <span style={{ color: '#ff3b30', fontWeight: 900 }}>{label.slice(4)}</span></>
            : label}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px' }}>
          <div className="profile-stat" style={{ padding: '4px 2px' }}>
            <span className="profile-stat-value" style={{ fontSize: '16px', color: 'white' }}>{now || '—'}</span>
            <span className="profile-stat-label" style={{ fontSize: '8px', letterSpacing: '0px' }}>NOW</span>
          </div>
          <div className="profile-stat" style={{ padding: '4px 2px' }}>
            <span className="profile-stat-value" style={{ fontSize: '16px', color: 'white' }}>{best || 0}</span>
            <span className="profile-stat-label" style={{ fontSize: '8px', letterSpacing: '0px' }}>BEST</span>
          </div>
        </div>
        {strikes !== undefined && (
          <div style={{ textAlign: 'center', marginTop: '4px', color: 'var(--text-dim)' }}>
            <div style={{ fontSize: '10px', letterSpacing: '0.5px', textTransform: 'none' }}>
              stri<span style={{ color: '#ff3b30', fontWeight: 900, fontSize: '1.1em', verticalAlign: 'middle' }}>K</span>e: {strikes >= 1 ? strikes : '-'}
            </div>
            <div style={{ fontSize: '7px', letterSpacing: '0.3px', marginTop: '1px' }}>(3 strikes your streak resets)</div>
          </div>
        )}
      </div>
    );
    return (
      <>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
          {[
            { emoji: '🏈', label: 'NFL', now: profile.current_streak, best: profile.longest_streak },
            { emoji: '🏀', label: 'NBA', now: profile.nba_current_streak, best: profile.nba_longest_streak },
            { emoji: '🍲', label: 'MISH MOSH', now: profile.mosh_current_streak, best: profile.mosh_longest_streak },
          ].map(cell)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginTop: '8px' }}>
          {[
            { emoji: '👴', label: 'NTG SR.', now: profile.ntg_sr_current_streak, best: profile.ntg_sr_longest_streak, strikes: profile.ntg_sr_strikes || 0 },
            { emoji: '👶', label: 'NTG JR.', now: profile.ntg_current_streak, best: profile.ntg_longest_streak, strikes: profile.ntg_strikes || 0 },
            { emoji: '#️⃣', label: 'DIGITS', now: profile.digits_current_streak, best: profile.digits_longest_streak, strikes: profile.digits_strikes || 0 },
          ].map(cell)}
        </div>
      </>
    );
  })()}
</div>

{/* Scouting Report | Accuracy Ledger: career hit rate per mode, from
    get_my_accuracy(). Jr/Sr/Guy Off are retroactive (their tables predate
    this); the NFL/NBA/Mosh bars accumulate from daily_results go-live
    (2026-08-24) forward. Hidden entirely until there is at least one answer. */}
{(() => {
  if (!accuracy) return null;
  const d = accuracy.daily || {};
  // Guy Off deliberately omitted (Matt, 2026-08-24): H2H answers dwarf the
  // dailies and drag the headline down, it plays under different conditions
  // (opponent pressure, hard-mode typing, sudden death), and the W-L record
  // right below this section already tells that story. The RPC still returns
  // accuracy.guyoff if we ever want it back.
  const rows = [
    { emoji: '🏈', label: 'NFL',  ...(d.nfl   || { correct: 0, total: 0 }) },
    { emoji: '🏀', label: 'NBA',  ...(d.nba   || { correct: 0, total: 0 }) },
    { emoji: '🍲', label: 'MOSH', ...(d.mosh  || { correct: 0, total: 0 }) },
    { emoji: '👴', label: 'SR.',  ...(accuracy.sr || { correct: 0, total: 0 }) },
    { emoji: '👶', label: 'JR.',  ...(accuracy.jr || { correct: 0, total: 0 }) },
    { emoji: '#️⃣', label: 'DIGITS', ...(accuracy.digits || { correct: 0, total: 0 }) },
  ];
  const totC = rows.reduce((a, r) => a + (r.correct || 0), 0);
  const totT = rows.reduce((a, r) => a + (r.total || 0), 0);
  if (!totT) return null;
  const pct = (c, t) => (t ? Math.round((c / t) * 100) : 0);
  // Cold/warm/hot: 0-33 red, 34-66 gold, 67-100 green. The headline uses the
  // muted variant so it reads as a different weight from the saturated bars.
  const heat = (p) => (p >= 67 ? 'var(--green-correct)' : p >= 34 ? 'var(--gold)' : 'var(--red)');
  const heatDull = (p) => (p >= 67 ? '#5fae74' : p >= 34 ? '#b3a06a' : '#b35050');
  return (
    <div style={{ padding: '8px 12px 10px', background: 'var(--bg-navy)', borderTop: '1px solid var(--border-dark)' }}>
      <div style={{ fontFamily: 'var(--font-heading)', fontSize: '9px', letterSpacing: '3px', color: 'var(--text-dim)', marginBottom: '4px', textAlign: 'center' }}>SCOUTING REPORT | ACCURACY LEDGER</div>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: '8px', marginBottom: '8px' }}>
        <span style={{ fontFamily: 'var(--font-heading)', fontSize: '22px', fontWeight: 700, color: heatDull(pct(totC, totT)) }}>{pct(totC, totT)}%</span>
        <span style={{ fontFamily: 'var(--font-body)', fontSize: '10px', color: 'var(--text-dim)' }}>{totC} of {totT} guys called</span>
      </div>
      {rows.filter(r => r.total > 0).map(r => (
        <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '6px 0' }}>
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: '10px', letterSpacing: '1px', color: 'var(--text-white)', width: '76px', flexShrink: 0 }}>{r.emoji} {r.label}</span>
          <div style={{ flex: 1, height: '8px', background: '#111b30', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct(r.correct, r.total)}%`, background: heat(pct(r.correct, r.total)), borderRadius: '4px', opacity: 0.9 }} />
          </div>
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', color: heat(pct(r.correct, r.total)), width: '84px', textAlign: 'right', flexShrink: 0 }}>
            {pct(r.correct, r.total)}% <span style={{ color: 'var(--text-dim)', fontSize: '9px', fontFamily: 'var(--font-body)' }}>({r.correct}/{r.total})</span>
          </span>
        </div>
      ))}
      <div style={{ textAlign: 'center', fontFamily: 'var(--font-body)', fontSize: '8px', color: 'var(--text-dim)', opacity: 0.7, marginTop: '6px' }}>
        Tracking began: Sr. Jul 28 · Jr. Aug 5 · NFL, NBA + Mosh Aug 24, 2026. Streaks earned earlier count toward BEST, not the ledger.
      </div>
    </div>
  );
})()}

{/* Head-to-head record — minimized footnote */}
<div style={{ padding: '8px 12px', background: 'var(--bg-navy)', borderTop: '1px solid var(--border-dark)', textAlign: 'center' }}>
  <span style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', letterSpacing: '1px', color: 'var(--text-dim)' }}>
    <strong style={{ color: '#9aa6b4', fontWeight: 900 }}>⚔️ GUY OFF</strong> · {profile.wins}W · {profile.losses}L · {profile.games_played} PLAYED · {winPct}% WIN
  </span>
</div>

      {claimTier && (
        <ClaimModal
          tier={claimTier}
          onClaimed={loadProfile}
          onClose={() => setClaimTier(null)}
        />
      )}
    </div>
  );}

export default Profile;